<script setup>
// NOTICE: KAPTION-INTEGRATION
// conversation-tabs.STRIP.1-5, TABS.1-4, OPEN.3, KEYS.1-3
//
// The strip above the conversation. It renders from the store every time, so a
// name change or a new unread count lands here without anyone syncing it, and
// a tab is only ever a conversation id.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import { useKaptionDesktop } from 'dashboard/composables/useKaptionDesktop';
import { useKaptionConversationTabs } from 'dashboard/composables/useKaptionConversationTabs';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { tabsBridge } = useKaptionDesktop();
const {
  tabs,
  activeId,
  restoreTabs,
  pruneTabs,
  openTab,
  closeTab,
  reopenClosedTab,
  activateTab,
  selectTabAt,
  cycleTab,
  moveTab,
} = useKaptionConversationTabs();

const stripRef = ref(null);
const crowded = ref(false);
const draggedId = ref(null);
const dropTargetId = ref(null);
const dropAfter = ref(false);

const currentChat = useMapGetter('getSelectedChat');
const allConversations = useMapGetter('getAllConversations');

const conversationFor = id =>
  store.getters.getConversationById(id) ||
  allConversations.value.find(chat => chat.id === Number(id)) ||
  null;

const openTabs = computed(() =>
  tabs.value.map(tab => {
    const chat = conversationFor(tab.id);
    const sender = chat?.meta?.sender || {};
    return {
      id: tab.id,
      name: sender.name || t('KAPTION_CONVERSATION_TABS.UNTITLED'),
      thumbnail: sender.thumbnail || '',
      // The number, and only the number: it is what tells two conversations
      // apart. The inbox is the same on all of them, so it earned no room.
      subtitle: sender.phone_number || '',
      unread: chat?.unread_count || 0,
      resolved: Boolean(chat),
    };
  })
);

// conversation-tabs.STRIP.4 — measured against the real strip, and applied as a
// class so the change is instant. A width transition here would sweep tabs
// under the window controls on the way past.
const measureCrowding = () => {
  const strip = stripRef.value;
  if (!strip) return;
  // 124px is the narrowest a tab can be and still show a name worth reading;
  // below that the inactive ones are better off as avatars.
  crowded.value = tabs.value.length * 140 > strip.clientWidth - 24;
};

let observer = null;
let unsubscribeCommands = null;

onMounted(() => {
  restoreTabs(props.accountId);
  // conversation-tabs.TABS.5 — anything the store cannot resolve is dropped, but
  // only once it has had a chance to load, so a cold start keeps its tabs.
  if (allConversations.value.length) {
    pruneTabs(id => Boolean(conversationFor(id)));
  }
  if (currentChat.value?.id) openTab(currentChat.value.id);
  measureCrowding();
  if (typeof ResizeObserver !== 'undefined' && stripRef.value) {
    observer = new ResizeObserver(measureCrowding);
    observer.observe(stripRef.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  tabsBridge.value?.setEnabled?.(false);
  unsubscribeCommands?.();
});

watch(
  () => tabs.value.length,
  () => measureCrowding()
);

// conversation-tabs.TABS.1 — arriving at a conversation any other way (a
// notification, a deep link, the picker) adopts it into the strip.
watch(
  () => currentChat.value?.id,
  id => {
    if (id) openTab(id);
  }
);

const goTo = id => {
  const chat = conversationFor(id);
  const path = frontendURL(
    conversationUrl({ accountId: props.accountId, id: Number(id) })
  );
  activateTab(id);
  if (chat?.id !== currentChat.value?.id) router.push({ path });
};

const onTabClick = id => {
  if (Number(id) === Number(activeId.value)) return;
  goTo(id);
};

const onClose = id => {
  const next = closeTab(id);
  if (next) {
    goTo(next);
    return;
  }
  // conversation-tabs.TABS.3 — closing the last tab leaves nothing open, so the
  // view goes back to having no conversation selected rather than keeping the
  // one that was just closed on screen under an empty strip. The list route is
  // whatever the current one is without its conversation, so an inbox, a label
  // or a team keeps its context.
  const listPath = route.path.replace(/\/conversations\/\d+.*$/, '');
  store.dispatch('clearSelectedState');
  router.push({
    path: listPath || frontendURL(`accounts/${props.accountId}/dashboard`),
  });
};

// conversation-tabs.OPEN.3 — middle-click closes, the way a browser does.
const onAuxClick = (event, id) => {
  if (event.button !== 1) return;
  event.preventDefault();
  onClose(id);
};

const onDragStart = id => {
  draggedId.value = id;
};

const onDragOver = (event, id) => {
  event.preventDefault();
  if (draggedId.value === null || id === draggedId.value) return;
  const rect = event.currentTarget.getBoundingClientRect();
  dropTargetId.value = id;
  dropAfter.value = event.clientX > rect.left + rect.width / 2;
};

const onDrop = () => {
  if (draggedId.value === null || dropTargetId.value === null) return;
  const to = tabs.value.findIndex(tab => tab.id === dropTargetId.value);
  moveTab(draggedId.value, dropAfter.value ? to + 1 : to);
  draggedId.value = null;
  dropTargetId.value = null;
};

const onDragEnd = () => {
  draggedId.value = null;
  dropTargetId.value = null;
};

// conversation-tabs.KEYS.1-3 — the shell owns the accelerators and sends what
// it matched; the page is what knows the tab list.
const runCommand = command => {
  if (!command || typeof command !== 'object') return;
  if (command.type === 'select') {
    const id = selectTabAt(Number(command.index));
    if (id) goTo(id);
    return;
  }
  if (command.type === 'cycle') {
    const id = cycleTab(command.step === -1 ? -1 : 1);
    if (id) goTo(id);
    return;
  }
  if (command.type === 'close-active') {
    if (activeId.value) onClose(activeId.value);
    return;
  }
  if (command.type === 'reopen-closed') {
    const id = reopenClosedTab();
    if (id) goTo(id);
    return;
  }
  if (command.type === 'open-url') {
    const match = /\/conversations\/(\d+)/.exec(String(command.url || ''));
    if (!match) return;
    openTab(match[1], { background: command.background !== false });
    if (command.background === false) goTo(match[1]);
  }
};

// conversation-tabs.KEYS.3 — the shell stays out of the keyboard until the
// strip says it is showing, so nothing changes for anyone without the feature.
onMounted(() => {
  const bridge = tabsBridge.value;
  if (!bridge) return;
  bridge.setEnabled?.(true);
  unsubscribeCommands = bridge.onCommand?.(runCommand) || null;
});

const closeLabel = id =>
  t('KAPTION_CONVERSATION_TABS.CLOSE', {
    name: openTabs.value.find(tab => tab.id === id)?.name || '',
  });
</script>

<template>
  <div
    v-if="openTabs.length"
    ref="stripRef"
    data-testid="kaption-conversation-tabs"
    class="wp-conversation-tabs kaption-tabs flex items-center flex-shrink-0 gap-1 p-1 mb-2 overflow-hidden border rounded-xl bg-n-solid-1 border-n-weak"
    :class="{ 'kaption-tabs--crowded': crowded }"
  >
    <button
      v-for="tab in openTabs"
      :key="tab.id"
      type="button"
      draggable="true"
      data-testid="kaption-conversation-tab"
      :data-tab-id="tab.id"
      :title="tab.name"
      :aria-current="tab.id === activeId ? 'page' : undefined"
      class="wp-conversation-tab kaption-tab relative flex items-center h-9 min-w-0 gap-2 py-1 pl-2 pr-1 text-sm rounded-lg outline outline-1 transition-colors"
      :class="[
        tab.id === activeId
          ? 'kaption-tab--active bg-n-solid-active outline-n-container dark:outline-transparent text-n-slate-12'
          : 'bg-transparent outline-transparent text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12',
        draggedId === tab.id ? 'opacity-40' : '',
        dropTargetId === tab.id
          ? dropAfter
            ? 'kaption-tab--drop-after'
            : 'kaption-tab--drop-before'
          : '',
      ]"
      @click="onTabClick(tab.id)"
      @auxclick="onAuxClick($event, tab.id)"
      @dragstart="onDragStart(tab.id)"
      @dragover="onDragOver($event, tab.id)"
      @drop="onDrop"
      @dragend="onDragEnd"
    >
      <Avatar
        :name="tab.name"
        :src="tab.thumbnail"
        :size="20"
        rounded-full
        class="flex-shrink-0"
      />
      <span
        v-if="tab.unread"
        data-testid="kaption-conversation-tab-unread"
        class="absolute grid text-xs font-semibold rounded-full pointer-events-none left-5 top-0.5 min-w-4 h-4 place-content-center px-1 bg-n-ruby-9 text-white"
      >
        {{ tab.unread > 9 ? $t('KAPTION_CONVERSATION_TABS.MANY') : tab.unread }}
      </span>
      <span class="kaption-tab__copy flex flex-col min-w-0 text-left">
        <span class="text-sm font-medium leading-tight truncate">
          {{ tab.name }}
        </span>
        <span
          v-if="tab.subtitle"
          class="kaption-tab__channel text-[0.6875rem] leading-tight truncate opacity-70"
        >
          {{ tab.subtitle }}
        </span>
      </span>
      <span
        role="button"
        tabindex="-1"
        data-testid="kaption-conversation-tab-close"
        class="grid flex-shrink-0 rounded size-4 place-content-center hover:bg-n-alpha-2"
        :aria-label="closeLabel(tab.id)"
        @click.stop="onClose(tab.id)"
      >
        <Icon icon="i-lucide-x" class="size-3" />
      </span>
    </button>
  </div>
</template>

<style scoped>
/* Mechanics only. Every Warm Paper appearance rule for this strip lives in
   assets/scss/_warm-paper.scss under `.warm-paper`, the way that file's
   contract requires: the template carries hook classes and nothing else. */
.kaption-tabs {
  container-type: inline-size;
}

.kaption-tab {
  width: 13.5rem;
  min-width: 8rem;
  max-width: 15rem;
  flex: 0 1 13.5rem;
  cursor: grab;
}

.kaption-tab:active {
  cursor: grabbing;
}

/* The channel is the first thing to go: a name and an unread count are what
   tell one tab from another. */
@container (max-width: 640px) {
  .kaption-tab__channel {
    display: none;
  }
}

.kaption-tab [data-testid='kaption-conversation-tab-close'] {
  opacity: 0;
}

.kaption-tab:hover [data-testid='kaption-conversation-tab-close'],
.kaption-tab[aria-current='page']
  [data-testid='kaption-conversation-tab-close'] {
  opacity: 0.7;
}

.kaption-tab--drop-before {
  box-shadow: inset 3px 0 0 rgba(var(--brand), 1);
}

.kaption-tab--drop-after {
  box-shadow: inset -3px 0 0 rgba(var(--brand), 1);
}

/* conversation-tabs.STRIP.4 — out of room: the inactive tabs give up their
   text, the active one keeps it, and nothing animates on the way. */
.kaption-tabs--crowded .kaption-tab:not([aria-current='page']) {
  width: 2.25rem;
  min-width: 2.25rem;
  max-width: 2.25rem;
  flex-basis: 2.25rem;
  justify-content: center;
  padding-inline: 0.25rem;
}

.kaption-tabs--crowded
  .kaption-tab:not([aria-current='page'])
  .kaption-tab__copy,
.kaption-tabs--crowded
  .kaption-tab:not([aria-current='page'])
  .kaption-tabs--crowded
  .kaption-tab:not([aria-current='page'])
  [data-testid='kaption-conversation-tab-unread'] {
  left: auto;
  right: -0.125rem;
  top: -0.125rem;
}
</style>
